/**
 * Sportworks Theme Configuration
 * Based on Sportworks Brand Guide v3 (January 26, 2024)
 * Colors taken directly from brand specifications
 */

import { ThemeConfig } from '../types';

export const sportworksTheme: ThemeConfig = {
  id: 'sportworks',
  name: 'Sportworks',
  description: 'Professional bike parking and transit solutions theme with dynamic yellow and sophisticated grays',
  
  // Brand colors from Sportworks Brand Guide
  colors: {
    // Primary Colors (from brand guide)
    richBlack: '#000000',
    mediumGrey: '#939598',
    white: '#FFFFFF',
    
    // SW Yellow variations
    swYellow: '#FFC20E',
    lightSwYellow: '#FFDE91',
    xLightSwYellow: '#FFEDC5',
    
    // SW Gray variations  
    swGray: '#939598',
    lightSwGray: '#D1D3D4',
    darkSwGray: '#58595B',
    
    // Secondary Colors (from brand guide)
    deepOcean: '#346787',  // Blue base
    mist: '#B4CBD6',       // Blue light
    darkSky: '#1B4158',    // Blue dark
    stoneGrey: '#3F5C5A',  // Slate base
    
    // Tertiary Colors
    darkGrey: '#304A48',   // Slate dark
    ocean: '#99AFAC',      // Slate light
    lightGrey: '#D1D3D4',  // Light SW Gray
    
    // Action Colors (from secondary palette)
    warningRed: '#E57048',
    warningRedHover: '#98492B',
    warningRedActive: '#E7946F',
    successGreen: '#77A842',
    successGreenHover: '#156534',
    successGreenActive: '#C2EA81',
  },

  // CSS Variables mapping based on Sportworks brand guide
  cssVariables: {
    // Typography (from brand guide - Roboto Medium for headers, Arial for body)
    '--font-family-primary': '"Roboto", system-ui, sans-serif',
    '--font-family-mono': '"Roboto", monospace',
    
    // Sportworks brand colors
    '--sw-yellow': '#FFC20E',
    '--sw-yellow-light': '#FFDE91',
    '--sw-yellow-x-light': '#FFEDC5',
    '--sw-gray': '#939598',
    '--sw-gray-light': '#D1D3D4',
    '--sw-gray-dark': '#58595B',
    '--sw-white': '#FFFFFF',
    '--sw-black': '#000000',
    
    // Secondary colors
    '--sw-blue': '#346787',
    '--sw-blue-light': '#B4CBD6',
    '--sw-blue-dark': '#1B4158',
    '--sw-slate': '#3F5C5A',
    '--sw-slate-light': '#99AFAC',
    '--sw-slate-dark': '#304A48',
    '--sw-red': '#E57048',
    '--sw-red-light': '#E7946F',
    '--sw-red-dark': '#98492B',
    '--sw-green': '#77A842',
    '--sw-green-light': '#C2EA81',
    '--sw-green-dark': '#156534',
    
    // Semantic color mapping using Sportworks colors
    '--color-primary': '#FFC20E',
    '--color-primary-hover': '#FFDE91',
    '--color-primary-active': '#939598',
    '--color-primary-light': '#FFEDC5',
    
    '--color-success': '#77A842',
    '--color-success-hover': '#C2EA81',
    '--color-success-active': '#156534',
    '--color-success-light': '#C2EA81',
    
    '--color-warning': '#E57048',
    '--color-warning-hover': '#E7946F',
    '--color-warning-active': '#98492B',
    '--color-warning-light': '#E7946F',
    
    '--color-error': '#E57048',
    '--color-error-hover': '#E7946F',
    '--color-error-active': '#98492B',
    '--color-error-light': '#E7946F',
    
    // Text colors using SW brand palette
    '--color-text-primary': '#000000',
    '--color-text-secondary': '#58595B',
    '--color-text-tertiary': '#939598',
    '--color-text-disabled': '#D1D3D4',
    '--color-text-inverse': '#FFFFFF',
    
    // Background colors
    '--color-bg-primary': '#FFFFFF',
    '--color-bg-secondary': '#FFEDC5',
    '--color-bg-tertiary': '#D1D3D4',
    '--color-bg-disabled': '#D1D3D4',
    '--color-bg-inverse': '#000000',
    
    // Border colors
    '--color-border-primary': '#939598',
    '--color-border-secondary': '#D1D3D4',
    '--color-border-hover': '#58595B',
    '--color-border-focus': '#FFC20E',
    
    // Shadow colors using SW black
    '--shadow-light': '0 2px 8px rgba(0, 0, 0, 0.08)',
    '--shadow-medium': '0 4px 12px rgba(0, 0, 0, 0.12)',
    '--shadow-heavy': '0 6px 16px rgba(0, 0, 0, 0.16)',
    '--shadow-popover': '0 2px 8px rgba(0, 0, 0, 0.08)',
    
    // Overlay colors
    '--color-overlay-dark': 'rgba(0, 0, 0, 0.5)',
    '--color-overlay-light': 'rgba(255, 237, 197, 0.8)',
    
    // Layout colors
    '--color-layout-header': '#FFFFFF',
    '--color-layout-sidebar': '#FFEDC5',
    '--color-layout-content': '#FFFFFF',
    
    // Component specific colors
    '--color-card-bg': '#FFFFFF',
    '--color-card-border': '#D1D3D4',
    '--color-card-shadow': '0 2px 8px rgba(0, 0, 0, 0.08)',
    
    '--color-input-bg': '#FFFFFF',
    '--color-input-border': '#939598',
    '--color-input-hover': '#58595B',
    '--color-input-focus': '#FFC20E',
    
    '--color-button-default-bg': '#FFFFFF',
    '--color-button-default-border': '#939598',
    '--color-button-default-hover': '#FFEDC5',
    
    // Tag colors using SW palette
    '--color-tag-primary': '#FFC20E',
    '--color-tag-secondary': '#346787',
    '--color-tag-tertiary': '#3F5C5A',
    '--color-tag-muted': '#939598',
    '--color-tag-dark': '#58595B',
    
    // Interaction states
    '--color-hover-bg': 'rgba(255, 194, 14, 0.05)',
    '--color-active-bg': 'rgba(255, 194, 14, 0.10)',
    '--color-selected-bg': 'rgba(255, 194, 14, 0.15)',
    '--color-focus-outline': '#FFC20E',
    
    // Workspace specific
    '--color-workspace-bg': '#FFEDC5',
    '--color-workspace-sidebar': '#FFFFFF',
    '--color-workspace-border': '#D1D3D4',
    
    // Chat specific
    '--color-chat-bg': '#FFFFFF',
    '--color-chat-message-bg': '#FFFFFF',
    '--color-chat-message-border': '#D1D3D4',
    '--color-chat-message-shadow': '0 2px 8px rgba(0, 0, 0, 0.08)',
    '--color-chat-user-badge': '#FFC20E',
    '--color-chat-assistant-badge': '#346787',
    
    // Navigation
    '--color-nav-bg': '#FFFFFF',
    '--color-nav-item-hover': '#FFEDC5',
    '--color-nav-item-active': 'rgba(255, 194, 14, 0.10)',
    '--color-nav-text': '#000000',
    '--color-nav-text-secondary': '#58595B',
    
    // Status colors
    '--color-status-online': '#77A842',
    '--color-status-offline': '#58595B',
    '--color-status-warning': '#E57048',
    '--color-status-info': '#346787',
    
    // Email integration
    '--color-email-unread': '#FFC20E',
    '--color-email-read-opacity': '0.6',
    
    // MCP server colors
    '--color-mcp-connected': '#77A842',
    '--color-mcp-disconnected': '#58595B',
    '--color-mcp-category-bg': '#FFEDC5'
  },

  // Dark theme variants using Sportworks brand colors
  darkMode: {
    '--color-text-primary': '#FFFFFF',
    '--color-text-secondary': '#D1D3D4',
    '--color-text-tertiary': '#939598',
    '--color-text-disabled': '#58595B',
    '--color-text-inverse': '#000000',
    
    '--color-bg-primary': '#000000',
    '--color-bg-secondary': '#58595B',
    '--color-bg-tertiary': '#304A48',
    '--color-bg-disabled': '#304A48',
    '--color-bg-inverse': '#FFFFFF',
    
    '--color-border-primary': '#58595B',
    '--color-border-secondary': '#939598',
    
    '--color-card-bg': '#58595B',
    '--color-card-border': '#939598',
    
    '--color-workspace-bg': '#000000',
    '--color-workspace-sidebar': '#58595B',
    '--color-workspace-border': '#939598',
    
    '--color-chat-bg': '#000000',
    '--color-chat-message-bg': '#58595B',
    '--color-chat-message-border': '#939598',
    
    '--color-nav-bg': '#58595B',
    '--color-nav-item-hover': '#304A48',
    '--color-nav-item-active': 'rgba(255, 194, 14, 0.20)',
    '--color-nav-text': '#FFFFFF',
    '--color-nav-text-secondary': '#D1D3D4',
    
    '--color-hover-bg': 'rgba(255, 194, 14, 0.15)',
    '--color-active-bg': 'rgba(255, 194, 14, 0.25)',
    '--color-selected-bg': 'rgba(255, 194, 14, 0.30)',
    
    '--color-mcp-category-bg': '#304A48',
    
    '--color-layout-sidebar': '#58595B'
  },

  // Ant Design theme configuration using Sportworks colors
  antTheme: {
    token: {
      colorPrimary: '#FFC20E',
      colorPrimaryHover: '#FFDE91',
      colorPrimaryActive: '#939598',
      colorSuccess: '#77A842',
      colorWarning: '#E57048',
      colorError: '#E57048',
      colorInfo: '#346787',
      colorBgContainer: '#FFFFFF',
      colorBgElevated: '#FFEDC5',
      colorText: '#000000',
      colorTextSecondary: '#58595B',
      colorBorder: '#D1D3D4',
      fontFamily: '"Roboto", system-ui, sans-serif',
      borderRadius: 8
    },
    components: {
      Button: {
        primaryShadow: 'none',
        defaultShadow: 'none',
        fontWeight: 500
      },
      Card: {
        boxShadowTertiary: '0 2px 8px rgba(0, 0, 0, 0.08)',
        headerBg: '#FFEDC5'
      },
      Input: {
        activeBorderColor: '#FFC20E',
        hoverBorderColor: '#939598',
        activeShadow: 'none'
      },
      Select: {
        optionActiveBg: 'rgba(255, 194, 14, 0.10)',
        optionSelectedBg: 'rgba(255, 194, 14, 0.20)',
        optionSelectedColor: '#000000'
      }
    }
  }
};

export default sportworksTheme;